import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFilter } from '../context/FilterContext';
import DatePicker from './DatePicker';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFilter, FiX, FiCheck, FiRefreshCw } = FiIcons;

const GlobalFilters = ({ 
  showPerformanceFilter = false, 
  showChannelFilter = true, 
  showSearchFilter = true,
  showDateFilter = true 
}) => {
  const {
    filters,
    datePresets,
    performanceFilters,
    updateDateRange,
    updatePerformanceFilter,
    updateChannelFilter,
    updateSearchTerm,
    resetFilters
  } = useFilter();

  const [showPerformancePicker, setShowPerformancePicker] = useState(false);
  const [showChannelPicker, setShowChannelPicker] = useState(false);

  const channels = [
    { id: 'facebook', name: 'Facebook', icon: '📘' },
    { id: 'instagram', name: 'Instagram', icon: '📷' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵' },
    { id: 'youtube', name: 'YouTube', icon: '📺' }
  ];

  const getActiveFiltersCount = () => {
    let count = 0;
    if (showPerformanceFilter && filters.performance !== 'all') count++;
    if (filters.channels.length > 0) count++;
    if (filters.searchTerm) count++;
    if (showDateFilter && filters.dateRange.preset !== 'last30days') count++;
    return count;
  };

  const handleChannelToggle = (channelId) => {
    const newChannels = filters.channels.includes(channelId)
      ? filters.channels.filter(id => id !== channelId)
      : [...filters.channels, channelId];
    updateChannelFilter(newChannels);
  };

  const handleDateChange = (startDate, endDate) => {
    updateDateRange('custom', startDate, endDate);
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">ตัวกรอง</h3>
          {getActiveFiltersCount() > 0 && (
            <div className="flex items-center space-x-2">
              <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">
                {getActiveFiltersCount()} ตัวกรองที่ใช้
              </span>
              <motion.button
                onClick={resetFilters}
                className="text-gray-500 hover:text-red-600 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="ล้างตัวกรองทั้งหมด"
              >
                <SafeIcon icon={FiRefreshCw} className="h-4 w-4" />
              </motion.button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search Filter */}
          {showSearchFilter && (
            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหาสื่อโฆษณา..."
                value={filters.searchTerm}
                onChange={(e) => updateSearchTerm(e.target.value)}
                className="pl-4 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
              {filters.searchTerm && (
                <button
                  onClick={() => updateSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Date Range Filter */}
          {showDateFilter && (
            <DatePicker
              startDate={filters.dateRange.startDate}
              endDate={filters.dateRange.endDate}
              onDateChange={handleDateChange}
              presets={datePresets}
              showPresets={true}
            />
          )}

          {/* Performance Filter - Only shown when prop is true */}
          {showPerformanceFilter && (
            <div className="relative">
              <motion.button
                onClick={() => setShowPerformancePicker(!showPerformancePicker)}
                className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
                  filters.performance !== 'all'
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiFilter} className="h-4 w-4" />
                <span className="text-sm">
                  {performanceFilters.find(f => f.id === filters.performance)?.label || 'ประสิทธิภาพ'}
                </span>
              </motion.button>

              <AnimatePresence>
                {showPerformancePicker && (
                  <motion.div
                    className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-80"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">กรองตามประสิทธิภาพ</h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {performanceFilters.map((filter) => (
                          <button
                            key={filter.id}
                            onClick={() => {
                              updatePerformanceFilter(filter.id);
                              setShowPerformancePicker(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                              filters.performance === filter.id
                                ? 'bg-blue-100 text-blue-700'
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{filter.label}</div>
                                <div className="text-xs text-gray-500">{filter.description}</div>
                              </div>
                              {filters.performance === filter.id && (
                                <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Channel Filter */}
          {showChannelFilter && (
            <div className="relative">
              <motion.button
                onClick={() => setShowChannelPicker(!showChannelPicker)}
                className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
                  filters.channels.length > 0
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-sm">
                  ช่องทาง {filters.channels.length > 0 && `(${filters.channels.length})`}
                </span>
              </motion.button>

              <AnimatePresence>
                {showChannelPicker && (
                  <motion.div
                    className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-64"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">เลือกช่องทาง</h4>
                      <div className="space-y-2">
                        {channels.map((channel) => (
                          <label
                            key={channel.id}
                            className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg"
                          >
                            <input
                              type="checkbox"
                              checked={filters.channels.includes(channel.id)}
                              onChange={() => handleChannelToggle(channel.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-lg">{channel.icon}</span>
                            <span className="text-sm text-gray-900">{channel.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showPerformancePicker || showChannelPicker) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowPerformancePicker(false);
            setShowChannelPicker(false);
          }}
        />
      )}
    </motion.div>
  );
};

export default GlobalFilters;